import { useState, useCallback } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import {
  CashRegisterResponse,
  CashRegisterSummary,
  CashRegisterMovement,
  RevenueChartData,
  ReportFilters,
  BookingType,
} from '../../types/venue';

export const useReports = () => {
  const [cashRegister, setCashRegister] = useState<CashRegisterResponse | null>(null);
  const [revenueChart, setRevenueChart] = useState<RevenueChartData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReportFilters>({});

  // Fetch cash register data
  const fetchCashRegister = useCallback(async (customFilters?: ReportFilters) => {
    setLoading(true);
    setError(null);

    const activeFilters = customFilters || filters;
    const params = new URLSearchParams();

    if (activeFilters.dateFrom) params.append('dateFrom', activeFilters.dateFrom);
    if (activeFilters.dateTo) params.append('dateTo', activeFilters.dateTo);
    if (activeFilters.venueId) params.append('venueId', activeFilters.venueId);
    if (activeFilters.bookingType) params.append('bookingType', activeFilters.bookingType);

    const endpoint = `/owner/reports/cash-register${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiRequest<CashRegisterResponse>(endpoint);

    if (response.data) {
      setCashRegister(response.data);
    } else {
      setError(response.error || 'Error al cargar reporte de caja');
    }

    setLoading(false);
  }, [filters]);

  // Fetch revenue chart data
  const fetchRevenueChart = useCallback(async (customFilters?: ReportFilters) => {
    setLoading(true);
    setError(null);

    const activeFilters = customFilters || filters;
    const params = new URLSearchParams();

    if (activeFilters.timeRange) params.append('timeRange', activeFilters.timeRange);
    if (activeFilters.groupBy) params.append('groupBy', activeFilters.groupBy);
    if (activeFilters.venueId) params.append('venueId', activeFilters.venueId);

    const endpoint = `/owner/reports/revenue-chart${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiRequest<{ data: RevenueChartData[] }>(endpoint);

    if (response.data) {
      setRevenueChart(response.data.data);
    } else {
      setError(response.error || 'Error al cargar datos del gráfico');
    }

    setLoading(false);
  }, [filters]);

  // Fetch all report data at once
  const fetchAllReports = useCallback(async (customFilters?: ReportFilters) => {
    const activeFilters = customFilters || filters;
    await Promise.all([
      fetchCashRegister(activeFilters),
      fetchRevenueChart(activeFilters),
    ]);
  }, [filters, fetchCashRegister, fetchRevenueChart]);

  // Export to CSV
  const exportToCsv = useCallback(async (timeRange: string = '90d') => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/owner/analytics/export?timeRange=${timeRange}&format=csv`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al exportar datos');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reservas_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      return { success: false, error: 'Error al exportar datos' };
    }
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<ReportFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Calculate totals from movements
  const calculateTotals = useCallback(() => {
    if (!cashRegister) {
      return {
        totalRevenue: 0,
        byType: { ONLINE: 0, FIXED: 0, MANUAL: 0 },
        byVenue: {} as Record<string, number>,
      };
    }

    const byType: Record<BookingType, number> = {
      ONLINE: 0,
      FIXED: 0,
      MANUAL: 0,
    };

    const byVenue: Record<string, number> = {};

    cashRegister.movements.forEach((m) => {
      byType[m.bookingType] += m.amount;
      byVenue[m.venue] = (byVenue[m.venue] || 0) + m.amount;
    });

    return {
      totalRevenue: cashRegister.summary.totalRevenue,
      byType,
      byVenue,
    };
  }, [cashRegister]);

  // Get summary data
  const summary = cashRegister?.summary || {
    totalRevenue: 0,
    totalBookings: 0,
    onlineBookings: 0,
    fixedBookings: 0,
    manualBookings: 0,
    occupancyRate: 0,
  };

  // Get movements
  const movements = cashRegister?.movements || [];

  // Get available venues for filtering
  const venues = cashRegister?.venues || [];

  return {
    // State
    cashRegister,
    revenueChart,
    loading,
    error,
    filters,

    // Actions
    fetchCashRegister,
    fetchRevenueChart,
    fetchAllReports,
    exportToCsv,
    updateFilters,
    resetFilters,

    // Helpers
    calculateTotals,

    // Derived data
    summary,
    movements,
    venues,
  };
};
